import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const focusableSelector = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const useModalBehavior = (open, onClose, panelRef) => {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = 'hidden';
    const panel = panelRef.current;
    const first = panel?.querySelector(focusableSelector);
    (first || panel)?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onCloseRef.current?.(); return; }
      if (event.key !== 'Tab' || !panel) return;
      const items = [...panel.querySelectorAll(focusableSelector)];
      if (!items.length) return;
      const firstItem = items[0]; const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) { event.preventDefault(); lastItem.focus(); }
      else if (!event.shiftKey && document.activeElement === lastItem) { event.preventDefault(); firstItem.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); document.body.style.overflow = previousOverflow; previousFocus?.focus?.(); };
  }, [open, panelRef]);
};

export function AdminModalSurface({ open = true, onClose, children, backdropClassName = '', surfaceRef, ariaLabelledBy }) {
  const panelRef = useRef(null);
  useModalBehavior(open, onClose, panelRef);
  if (!open) return null;
  return createPortal(
    <div ref={surfaceRef} className={`fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-slate-950/75 p-3 backdrop-blur-sm ${backdropClassName}`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={ariaLabelledBy} tabIndex={-1} className="flex max-h-[calc(100vh-1.5rem)] w-full items-center justify-center outline-none">{children}</div>
    </div>,
    document.body
  );
}

export default function AdminModal({ open, title, description, onClose, children, footer, size = 'lg' }) {
  const panelRef = useRef(null);
  useModalBehavior(open, onClose, panelRef);

  if (!open) return null;
  const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' };
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/75 p-3 backdrop-blur-sm" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-modal-title"
        tabIndex={-1}
        className={`admin-card flex max-h-[calc(100vh-1.5rem)] w-full ${widths[size] || widths.lg} flex-col overflow-hidden rounded-2xl border border-cyan-400/20 shadow-2xl`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-400/20 px-5 py-4">
          <div>
            <h2 id="admin-modal-title" className="admin-text-primary text-xl font-black">{title}</h2>
            {description && <p className="admin-text-secondary mt-1 text-sm">{description}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close dialog" className="rounded-xl border border-slate-400/20 p-2 hover:border-cyan-400 hover:text-cyan-500">
            <X size={18} />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-3 border-t border-slate-400/20 px-5 py-4">{footer}</footer>}
      </section>
    </div>,
    document.body
  );
}
